import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Button, Dropdown, Input, Typography } from '@robinpowered/ui-kit';
import {
  FilterButton,
  FilterButtonContent,
  overlayPopupStyles,
} from '../styles';
import { useFilterOptionKeyboardNav } from '../hooks/useFilterOptionKeyboardNav';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuildings,
  faChevronDown,
  faMagnifyingGlass,
} from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import { useTranslation } from 'react-i18next';

export interface BuildingRow {
  id: string;
  name: string;
}

interface BuildingFilterProps {
  buildings: BuildingRow[];
  loading: boolean;
  selectedLocationId: string | null;
  onChange: (id: string | null) => void;
}

export const BuildingFilter: React.FC<BuildingFilterProps> = ({
  buildings,
  loading,
  selectedLocationId,
  onChange,
}) => {
  const { t } = useTranslation('globalNavigation', {
    keyPrefix: 'searchOverlay',
  });
  const [open, setOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const normalized = filterText.trim().toLowerCase();
  const visibleBuildings = normalized
    ? buildings.filter((building) =>
        building.name.toLowerCase().includes(normalized)
      )
    : buildings;

  // Close the dropdown and return focus to the trigger so Tab continues to the next
  // filter (Category) instead of falling back to the start of the modal.
  const closeDropdown = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  const selectBuilding = useCallback(
    (index: number) => {
      const building = visibleBuildings[index];
      if (!building) {
        return;
      }
      onChange(building.id === selectedLocationId ? null : building.id);
      closeDropdown();
    },
    [visibleBuildings, selectedLocationId, onChange, closeDropdown]
  );

  const { activeIndex } = useFilterOptionKeyboardNav({
    open,
    optionCount: visibleBuildings.length,
    onSelect: selectBuilding,
    onClose: closeDropdown,
  });

  // Keep the keyboard-highlighted option scrolled into view.
  useEffect(() => {
    if (activeIndex < 0) {
      return;
    }
    const options =
      listRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
    options?.[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const selectedBuildingName = buildings.find(
    (building) => building.id === selectedLocationId
  )?.name;

  const triggerLabel = selectedBuildingName
    ? t('buildingFilter.triggerSelected', { building: selectedBuildingName })
    : t('building');

  return (
    <Dropdown
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
      styles={overlayPopupStyles}
      // Unmount the popup on close so the input remounts (and re-runs autoFocus)
      // every time the dropdown opens.
      destroyOnHidden
      popupRender={() => (
        <FilterPanel role="group" aria-label={t('buildingFilter.groupLabel')}>
          <Input
            autoFocus
            aria-label={t('buildingFilter.searchLabel')}
            placeholder={t('buildingFilter.searchPlaceholder')}
            value={filterText}
            onChange={(event) => setFilterText(event.target.value)}
            prefix={<FontAwesomeIcon icon={faMagnifyingGlass} />}
          />
          <BuildingList ref={listRef} role="listbox">
            {loading && (
              <PanelMessage>{t('buildingFilter.loading')}</PanelMessage>
            )}
            {!loading && visibleBuildings.length === 0 && (
              <PanelMessage>{t('buildingFilter.empty')}</PanelMessage>
            )}
            {!loading &&
              visibleBuildings.map((building, index) => {
                const isSelected = building.id === selectedLocationId;
                const isActive = index === activeIndex;
                return (
                  <BuildingOption
                    key={building.id}
                    type="text"
                    role="option"
                    aria-selected={isSelected}
                    data-active={isActive}
                    onClick={() => selectBuilding(index)}
                  >
                    {building.name}
                  </BuildingOption>
                );
              })}
          </BuildingList>
        </FilterPanel>
      )}
    >
      <FilterButton ref={triggerRef} type="text">
        <FilterButtonContent>
          <FontAwesomeIcon icon={faBuildings} />
          <span>{triggerLabel}</span>
          <FontAwesomeIcon icon={faChevronDown} />
        </FilterButtonContent>
      </FilterButton>
    </Dropdown>
  );
};

const FilterPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 280px;
  padding: 8px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
`;

const BuildingList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 240px;
  overflow-y: auto;
`;

const BuildingOption = styled(Button)`
  justify-content: flex-start;
  width: 100%;
  min-height: 36px;
  text-align: left;

  &[aria-selected='true'] {
    background: #f5f5f5;
    font-weight: 600;
  }

  &&[data-active='true'] {
    background: #e6f4ff;
  }
`;

const PanelMessage = styled(Typography.Text)`
  padding: 8px;
  color: rgba(0, 0, 0, 0.72);
  font-size: 13px;
`;
